/** "Assurance questions about this figure" — a compact section for a receipt and a record page
 * (gate 27 / ASR-02, the frontend half of `?record_id=`).
 *
 * An assurance provider asks about a record; the person looking at that record's receipt should not
 * have to open the queue to find out that someone has. This reads the queue filtered to the record
 * and says how many questions there are and what state each is in, with a way into the queue.
 *
 * **Four outcomes of the read, and only one of them is silent for a good reason.**
 *
 * - *Not shown the queue* (403/404) — nothing. A sentence would confirm that a queue exists, and the
 *   server answers the same for a record outside an engagement as for one that never existed.
 * - *Answered, and empty* — nothing. Most figures have no question and a line saying so on each
 *   would be clutter; this is the one empty state that is not a claim about the company.
 * - *Answered, with questions* — the count and the list.
 * - *Failed* (5xx, dropped connection) — **said, with a retry.** Rendering this as empty is the
 *   defect `Loaded.tsx` is about: a failed read dressed as "nobody has asked", on the section that
 *   exists to say somebody has.
 *
 * **A link per question goes to the queue, not to the question.** `/assurance` has no per-request
 * address yet; saying so here would be copy about a feature, so the link just says where it goes.
 */

import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { KINDS, useAssuranceAbout } from "../api/assurance";
import { keepSearch } from "../screens/search";
import styles from "./AssuranceAbout.module.css";

export const HEADING = "Assurance questions about this figure";

function kindLabel(kind: string): string {
  return KINDS.find((k) => k.kind === kind)?.label ?? kind;
}

export function AssuranceAbout({
  recordId,
  frame,
}: {
  recordId: string;
  /** Wraps whatever is shown, so a surface can give it its own heading or step. It is called only
   * when there is something to show — never for loading, empty or not-visible. */
  frame: (children: ReactNode) => ReactNode;
}) {
  const read = useAssuranceAbout(recordId);

  if (read.isPending) return null;

  if (read.error) {
    return frame(
      <div role="alert" data-testid="assurance-about-failed" className={styles.failed}>
        <span>Could not load assurance questions.</span>
        <button type="button" className={styles.retry} onClick={() => void read.refetch()}>
          Try again
        </button>
      </div>,
    );
  }

  const requests = read.data;
  if (!requests || requests.length === 0) return null;

  const open = requests.filter((r) => r.state === "open" || r.state === "answered").length;
  return frame(
    <div data-testid="assurance-about">
      <p className={styles.summary}>
        {requests.length} {requests.length === 1 ? "question" : "questions"}
        {open > 0 ? `, ${open} still awaiting resolution` : ", none awaiting resolution"}
      </p>
      <ul className={styles.list}>
        {requests.map((request) => (
          <li key={request.id} className={styles.item}>
            <span>{kindLabel(request.kind)}</span>
            <span className={styles.state}>{request.state}</span>
            <Link
              to="/assurance"
              search={keepSearch}
              className={styles.link}
              aria-label={`Open the assurance queue: ${kindLabel(request.kind)}, ${request.state}`}
            >
              Open the assurance queue
            </Link>
          </li>
        ))}
      </ul>
    </div>,
  );
}
